"use client";
import {
  IconArrowLeft,
  IconArrowRight,
  IconPlayerPlay,
} from "@tabler/icons-react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import Image from "next/image";
import { useEffect, useState } from "react";

export const AnimatedTestimonials = ({ testimonials, autoplay = false }) => {
  const [active, setActive] = useState(0);

  // Valores de rotación determinísticos basados en el índice (evita Math.random y problemas de hidratación)
  const rotationValues = testimonials.map(
    (_, i) => ((i * 7 + 3) % 21) - 10,
  );

  const handleNext = () => {
    setActive((prev) => (prev + 1) % testimonials.length);
  };

  const handlePrev = () => {
    setActive((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const isActive = (index) => {
    return index === active;
  };

  useEffect(() => {
    if (autoplay) {
      const interval = setInterval(handleNext, 7000);
      return () => clearInterval(interval);
    }
  }, [autoplay]);

  const getRotationValue = (index) => {
    return rotationValues[index] || 0;
  };
  return (
    <div className="mx-auto max-w-sm px-4 py-8 font-sans antialiased md:max-w-6xl md:px-8 lg:px-12">
      <div className="relative grid grid-cols-1 gap-8 md:gap-16 md:grid-cols-2 items-center">
        {/* Sección de imágenes */}
        <div className="order-2 md:order-1">
          <div className="relative h-80 md:h-96 w-full">
            <AnimatePresence>
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.src}
                  initial={{
                    opacity: 0,
                    scale: 0.9,
                    z: -100,
                    rotate: getRotationValue(index),
                  }}
                  animate={{
                    opacity: isActive(index) ? 1 : 0.7,
                    scale: isActive(index) ? 1 : 0.95,
                    z: isActive(index) ? 0 : -100,
                    rotate: isActive(index) ? 0 : getRotationValue(index),
                    zIndex: isActive(index)
                      ? 40
                      : testimonials.length + 2 - index,
                    y: isActive(index) ? [0, -80, 0] : 0,
                  }}
                  exit={{
                    opacity: 0,
                    scale: 0.9,
                    z: 100,
                    rotate: getRotationValue(index),
                  }}
                  transition={{
                    duration: 0.4,
                    ease: "easeInOut",
                  }}
                  className="absolute inset-0 origin-bottom"
                >
                  <div className="relative h-full w-full">
                    <Image
                      src={testimonial.src}
                      alt={testimonial.name}
                      fill
                      draggable={false}
                      sizes="(max-width: 768px) 80vw, 500px"
                      className="rounded-3xl object-cover object-center shadow-2xl ring-1 ring-red-100"
                    />
                    {/* Overlay gradient */}
                    <div className="absolute inset-0 bg-gradient-to-t from-red-900/20 via-transparent to-transparent rounded-3xl" />
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>

        {/* Sección de contenido */}
        <div className="order-1 md:order-2 flex flex-col justify-center py-4 md:py-8">
          <motion.div
            key={active}
            initial={{
              y: 30,
              opacity: 0,
            }}
            animate={{
              y: 0,
              opacity: 1,
            }}
            exit={{
              y: -30,
              opacity: 0,
            }}
            transition={{
              duration: 0.4,
              ease: "easeInOut",
            }}
          >
            {/* Quote */}
            <motion.blockquote className="text-2xl md:text-3xl font-medium text-title mb-8 leading-relaxed">
              <span className="text-primary text-4xl">"</span>
              {testimonials[active].quote.split(" ").map((word, index) => (
                <motion.span
                  key={index}
                  initial={{
                    filter: "blur(10px)",
                    opacity: 0,
                    y: 5,
                  }}
                  animate={{
                    filter: "blur(0px)",
                    opacity: 1,
                    y: 0,
                  }}
                  transition={{
                    duration: 0.3,
                    ease: "easeInOut",
                    delay: 0.03 * index,
                  }}
                  className="inline-block"
                >
                  {word}&nbsp;
                </motion.span>
              ))}
              <span className="text-primary text-4xl">"</span>
            </motion.blockquote>

            {/* Patient info */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xl font-bold text-title mb-1">
                  {testimonials[active].name}
                </p>
                <p className="text-primary font-medium">
                  {testimonials[active].treatment}
                </p>

                {/* Video button - si existe videoUrl */}
                {testimonials[active].videoUrl && (
                  <Dialog>
                    <DialogTrigger asChild>
                      <button className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-red-700 transition-colors duration-200">
                        <IconPlayerPlay className="w-4 h-4 fill-current" />
                        Ver video testimonial
                      </button>
                    </DialogTrigger>
                    <DialogContent className="max-w-4xl w-full p-0 bg-black">
                      <DialogTitle className="sr-only">
                        Video Testimonial
                      </DialogTitle>
                      <div className="relative w-full aspect-video">
                        <iframe
                          src={testimonials[active].videoUrl}
                          title={`Video testimonial de ${testimonials[active].name}`}
                          className="w-full h-full rounded-lg"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    </DialogContent>
                  </Dialog>
                )}
              </div>

              {/* Navigation buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handlePrev}
                  aria-label="Testimonio anterior"
                  className="group flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-r from-primary to-red-600 text-white shadow-lg hover:shadow-xl hover:shadow-primary/30 transition-all duration-300 hover:scale-110 active:scale-95"
                >
                  <IconArrowLeft className="h-5 w-5 transition-transform duration-300 group-hover:-translate-x-1" />
                </button>
                <button
                  onClick={handleNext}
                  aria-label="Siguiente testimonio"
                  className="group flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-r from-primary to-red-600 text-white shadow-lg hover:shadow-xl hover:shadow-primary/30 transition-all duration-300 hover:scale-110 active:scale-95"
                >
                  <IconArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            </div>
          </motion.div>

          {/* Progress indicators */}
          <div className="flex gap-2 mt-8 items-center">
            {testimonials.map((_, index) => (
              <div
                key={index}
                onClick={() => setActive(index)}
                className={`h-4 md:h-2 cursor-pointer rounded-full transition-all duration-300 ${index === active ? "w-10 md:w-8 bg-primary" : "w-4 md:w-2 bg-red-200 hover:bg-red-300"}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
